/**
 * Build per-strike rows with dynamic keys per selected expiry for stacked Recharts bars.
 * Used when two or more specific expiries are selected (not "all").
 */
import { ScannerData } from '../types/ScannerData';
import { DataMode } from '../types/DataMode';
import { getCBOEFieldValue } from './dataModeUtils';
import { computeSpotGammaGexMme, spotGammaToDisplayIndex } from './gammaCalculations';
import { expirySingleMatchesSelection } from './expiryFilterUtils';
import { sortByStrikePrice } from './filterUtils';

function num(item: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = item[k];
    if (v === null || v === undefined || v === '' || v === '-') continue;
    const x = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(x)) return x;
  }
  return 0;
}

function rowSide(item: Record<string, unknown>): 'C' | 'P' {
  const r = String(item['Option Type'] ?? item.optionType ?? '').toUpperCase();
  return r === 'C' || r === 'CALL' ? 'C' : 'P';
}

/** Stable ASCII key for Recharts `dataKey` (no brackets/dots issues). */
export function expiryLabelToStackBase(expiryLabel: string): string {
  const d = String(expiryLabel).replace(/\D/g, '').slice(0, 8);
  if (d.length >= 8) return `x${d}`;
  const slug = String(expiryLabel)
    .replace(/\W+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 32);
  return slug || 'exp';
}

/** Multi-expiry stacked mode: two or more specific expiries (not "all"). */
export function selectedStackExpiries(selectedExpiries: string[]): string[] | null {
  const spec = selectedExpiries.filter((e) => e !== 'all');
  if (spec.length < 2) return null;
  const digits = (s: string) => String(s).replace(/\D/g, '').slice(0, 8);
  return spec.slice().sort((a, b) => digits(a).localeCompare(digits(b)));
}

export function expiryPaletteColors(count: number, isDark: boolean): string[] {
  if (count <= 0) return [];
  const saturation = isDark ? 72 : 70;
  const lightness = isDark ? 58 : 50;
  return Array.from({ length: count }, (_, i) => {
    const hue = Math.round((i * 360) / Math.max(count, 1)) % 360;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  });
}

/** Short label for legend / tooltips (MM/DD when YYYYMMDD parseable). */
export function shortenExpiryLegend(label: string): string {
  const d = label.replace(/\D/g, '').slice(0, 8);
  if (d.length === 8) return `${d.slice(4, 6)}/${d.slice(6)}`;
  return label.length > 16 ? `${label.slice(0, 14)}…` : label;
}

export type StrikeExpiryStackRow = Record<string, number | string>;

/**
 * Aggregates filtered scanner rows into one chart row per strike with keys:
 * vol_${base}, oi_${base}, sgDisp_${base}, gex_${base}, callVol_/putVol_/callOI_/putOI_ per base.
 */
export function buildStrikeExpiryStackRows(
  filtered: ScannerData[],
  stackExpiryOrder: string[],
  dataMode: DataMode,
  underlyingPriceFallback: number,
  sideFilter: string,
): StrikeExpiryStackRow[] {
  const S = underlyingPriceFallback > 0 ? underlyingPriceFallback : 100;
  const bases = stackExpiryOrder.map(expiryLabelToStackBase);

  const map = new Map<number, Record<string, number>>();

  const ensure = (strike: number): Record<string, number> => {
    if (!map.has(strike)) {
      const o: Record<string, number> = {};
      for (const b of bases) {
        o[`vol_${b}`] = 0;
        o[`oi_${b}`] = 0;
        o[`sgRaw_${b}`] = 0;
        o[`gex_${b}`] = 0;
        o[`volGex_${b}`] = 0;
        o[`callVol_${b}`] = 0;
        o[`putVol_${b}`] = 0;
        o[`callOI_${b}`] = 0;
        o[`putOI_${b}`] = 0;
      }
      map.set(strike, o);
    }
    return map.get(strike)!;
  };

  for (const row of filtered) {
    const matched = stackExpiryOrder.find((sel) => expirySingleMatchesSelection(row['Expiry Date'], sel));
    if (!matched) continue;

    const r = row as unknown as Record<string, unknown>;
    const base = expiryLabelToStackBase(matched);
    const st = num(r, 'Strike', 'Strike Price') || 0;
    if (!st) continue;

    const s = rowSide(r);
    if (sideFilter === 'C' && s !== 'C') continue;
    if (sideFilter === 'P' && s !== 'P') continue;

    let vol = Number(getCBOEFieldValue(row, 'Volume')) || 0;
    if (vol === 0) vol = num(r, 'Bid Volume accumelated', 'Bid Volume') + num(r, 'Ask Volume accumelated', 'Ask Volume');
    const oi = getCBOEFieldValue(row, 'Open Interest');
    const { spotGamma, gex, volumeGex } = computeSpotGammaGexMme(row, dataMode, underlyingPriceFallback);

    const cur = ensure(st);
    cur[`vol_${base}`] += vol;
    cur[`oi_${base}`] += oi;
    cur[`sgRaw_${base}`] += spotGamma;
    cur[`gex_${base}`] += gex;
    cur[`volGex_${base}`] += volumeGex;
    if (s === 'C') {
      cur[`callVol_${base}`] += vol;
      cur[`callOI_${base}`] += oi;
    } else {
      cur[`putVol_${base}`] += vol;
      cur[`putOI_${base}`] += oi;
    }
  }

  const rows: StrikeExpiryStackRow[] = [];
  for (const [strikeNum, row] of Array.from(map.entries())) {
    const strike = Number(strikeNum);
    const out: StrikeExpiryStackRow = { strike };
    for (const b of bases) {
      const rawSg = row[`sgRaw_${b}`] ?? 0;
      out[`vol_${b}`] = row[`vol_${b}`] ?? 0;
      out[`oi_${b}`] = row[`oi_${b}`] ?? 0;
      out[`sgDisp_${b}`] = spotGammaToDisplayIndex(Number(rawSg), S);
      out[`gex_${b}`] = row[`gex_${b}`] ?? 0;
      out[`volGex_${b}`] = row[`volGex_${b}`] ?? 0;
      out[`callVol_${b}`] = row[`callVol_${b}`] ?? 0;
      out[`putVol_${b}`] = row[`putVol_${b}`] ?? 0;
      out[`putVolNeg_${b}`] = -(row[`putVol_${b}`] ?? 0);
      out[`callOI_${b}`] = row[`callOI_${b}`] ?? 0;
      out[`putOI_${b}`] = row[`putOI_${b}`] ?? 0;
      out[`putOINeg_${b}`] = -(row[`putOI_${b}`] ?? 0);
    }
    rows.push(out);
  }

  return sortByStrikePrice(rows as { strike: number }[]) as StrikeExpiryStackRow[];
}
